import React, { useCallback } from 'react';
import { styles } from '../styles/styles';

/**
 * Premium autocomplete input component with Apple-inspired design
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Input label text
 * @param {string} props.value - Current input value
 * @param {Function} props.onChange - onChange handler function
 * @param {Array} props.options - Filtered options to display
 * @param {Function} props.onSelect - Function to call when option is selected
 * @param {Function} props.onFocus - onFocus handler function
 * @param {Function} props.onBlur - onBlur handler function
 * @param {string} props.placeholder - Input placeholder text
 */
const AutocompleteInput = ({
    label,
    value,
    onChange,
    options = [],
    onSelect,
    onFocus,
    onBlur,
    placeholder = "",
    disabled = false
}) => {
    const handleClear = useCallback(() => {
        onChange({ target: { value: '' } });
        onFocus();
    }, [onChange, onFocus]);

    const handlePreventDefault = useCallback((e) => {
        e.preventDefault();
    }, []);

    const handleOptionSelect = useCallback((option) => {
        onSelect(option);
    }, [onSelect]);

    return (
        <div style={styles.formGroup}>
            <label style={styles.label}>{label}</label>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    value={value}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    style={styles.input}
                    disabled={disabled}
                />
                {value && (
                    <button
                        type="button"
                        onClick={handleClear}
                        onMouseDown={handlePreventDefault}
                        style={styles.inputControls.clearButton}
                        title="Clear input"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                )}
            </div>

            {options.length > 0 && (
                <ul style={styles.autocomplete}>
                    {options.map((option, idx) => (
                        <li
                            key={idx}
                            onClick={() => handleOptionSelect(option)}
                            style={{
                                ...styles.autocompleteItem,
                                backgroundColor: option === value ? `${styles.colors.primary}1A` : styles.autocompleteItem.backgroundColor
                            }}
                            onMouseDown={handlePreventDefault}
                        >
                            {option}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AutocompleteInput; 